// src/blogs/postLinks.js
// Everything about pointing at a post: which blog a post id lives in, the URL
// for it, and how to open it.
//
// A post's permalink is its blog's route plus the post id as a hash, e.g.
//   /resume/blog/thoughts#story-001
// Loading that URL — or navigating to it in-app — scrolls the feed to the
// post (BlogShell reads the hash; InfiniteBlogFeed does the scrolling). The
// hash form keeps the existing pathname router untouched, and works on a
// static host without any server-side route.
//
// Because a link only carries the id, ids must be unique across ALL three
// blogs' posts (a duplicate is warned about in dev).
import { audioPosts, moviePosts, thoughtsPosts } from "./blogData.js";
import { navigate, toHref, toRoute } from "../router.js";

// The single source of truth for blog scene routes — App.jsx (routing) and
// HeadScene.jsx (where the head sends you) both read these.
export const BLOG_ROUTES = {
  longer: "/blog/longer",
  content: "/blog/content",
  thoughts: "/blog/thoughts",
};

const postRoutes = new Map(); // post id -> the route of the blog it lives in
for (const [route, posts] of [
  [BLOG_ROUTES.longer, audioPosts],
  [BLOG_ROUTES.content, moviePosts],
  [BLOG_ROUTES.thoughts, thoughtsPosts],
]) {
  for (const post of posts) {
    if (postRoutes.has(post.id) && import.meta.env.DEV) {
      console.warn(`[blog] duplicate post id "${post.id}" — links to it will be ambiguous`);
    }
    postRoutes.set(post.id, route);
  }
}

/** The id of the DOM element wrapping a post in the feed. */
export function postDomId(id) {
  return `post-${id}`;
}

/** href for a post's permalink, or null if there's no such post. */
export function postHref(id) {
  const route = postRoutes.get(id);
  return route ? toHref(`${route}#${encodeURIComponent(id)}`) : null;
}

/** The post id named by the current URL's hash, if any. */
export function readHashPostId() {
  if (typeof window === "undefined") return null;
  const raw = window.location.hash.slice(1);
  if (!raw) return null;
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

/**
 * Open a post from anywhere in the app. Within the same blog this just moves
 * the URL's hash (BlogShell notices and scrolls). Across blogs it changes
 * route too — through `veilNavigate` (App's page-veil hop) when given, so the
 * swap gets the same cover as every other scene change.
 */
export function openPost(id, veilNavigate) {
  const route = postRoutes.get(id);
  if (!route) return;
  const dest = `${route}#${encodeURIComponent(id)}`;
  if (veilNavigate && route !== toRoute(window.location.pathname)) veilNavigate(dest);
  else navigate(dest);
}

/**
 * onClick for an <a href={postHref(id)}>: takes over plain left-clicks for
 * in-app navigation, but leaves ctrl/cmd/shift/middle-clicks to the browser
 * so "open in new tab" keeps working off the real href.
 */
export function handlePostLinkClick(event, id, veilNavigate) {
  if (event.defaultPrevented || event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  event.preventDefault();
  openPost(id, veilNavigate);
}
